import React, { useContext, useEffect, useRef } from 'react';

import { UNSELECTED } from 'shared/components/ihcl/button_select';
import { Center } from 'shared/components/ihcl/positioning';
import { styled, useStyletron } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';
import {
  Footnote,
  FootnoteIndicator,
} from 'shared/components/ihcl/v2/footnote';

import {
  OnboardingContext,
  OnboardingTitle,
  OnboardingWrapper,
  sendUpdate,
} from '../../containers/Onboarding';
import isReactivationFlow from '../../helpers/isReactivationFlow';
import { testimonial as onboardingTestimonial } from './OnboardingTimelineIsolatedQuestions';

const PermanentRoleWrapper = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: $theme.sizing.unit32,
}));

type OnboardingPermanentJobProps = {
  isLiteProfileActivating?: boolean;
  nextAction: string;
  stepNumber: number;
};
const OnboardingPermanentJob = ({
  nextAction,
  stepNumber,
  isLiteProfileActivating = false,
}: OnboardingPermanentJobProps) => {
  const {
    history,
    onboardingBasePath,
    onboardingData,
    onboardingUpdates,
    profileType = 'nurse',
    setOnboardingData,
    setOnboardingUpdates,
  } = useContext(OnboardingContext);
  const [, $theme] = useStyletron();
  const setOnboardingFields = (jobSearchStatus) => {
    if (isReactivationFlow(onboardingBasePath)) {
      setOnboardingUpdates({
        ...onboardingUpdates,
        job_search_status: jobSearchStatus,
      });
    } else {
      // If answer is NOT not looking, we need to set job_search_status to null to clear search status value
      const jobSearchStatusPayload = {
        job_search_status:
          jobSearchStatus === 'not_looking' ? jobSearchStatus : null,
      };
      setOnboardingData({
        ...onboardingData,
        permanent_job_search_status: jobSearchStatus,
        ...jobSearchStatusPayload,
      });
    }
  };

  useEffect(() => {
    const flowType = isReactivationFlow(onboardingBasePath)
      ? 'Reactivation'
      : 'Onboard';
    if (!isLiteProfileActivating) {
      setOnboardingFields(null);
      window.mixpanel.track(`${flowType} Step ${stepNumber} Started`, {
        step_name: 'Permanent Job',
      });
    } else {
      setOnboardingFields('just_started');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // sendUpdate responses mutate onboardingData, which re-runs this effect.
  // Track the last job_search_status we sent so each selection only PATCHes
  // once — a duplicate in-flight PATCH can otherwise resolve after the next
  // step has rendered and clobber edits made there with stale server state.
  const sentJobSearchStatus = useRef<string | null>(null);
  useEffect(() => {
    if (
      isReactivationFlow(onboardingBasePath) &&
      onboardingUpdates.job_search_status &&
      sentJobSearchStatus.current !== onboardingUpdates.job_search_status
    ) {
      sentJobSearchStatus.current = onboardingUpdates.job_search_status;
      sendUpdate({
        currentStep: stepNumber,
        history,
        nextAction,
        onboardingBasePath,
        onboardingData,
        onboardingUpdates,
        setOnboardingData,
        stepName: 'Permanent Job',
      });
    } else if (
      !isReactivationFlow(onboardingBasePath) &&
      onboardingData.permanent_job_search_status
    ) {
      history.push(nextAction);
      const flowType = isReactivationFlow(onboardingBasePath)
        ? 'Reactivation'
        : 'Onboard';
      window.mixpanel.track(`${flowType} Step ${stepNumber} Completed`, {
        step_name: 'Permanent Job',
      });
    }
  }, [
    history,
    nextAction,
    onboardingBasePath,
    onboardingData,
    onboardingUpdates,
    setOnboardingData,
    stepNumber,
  ]);

  const justifyContentStyle = { paddingTop: '78px' };

  return (
    <Center>
      <OnboardingWrapper
        $compactMaxWidth
        style={{ ...justifyContentStyle }}
        $justifyContent="space-between"
        $gap={$theme.sizing.unit32}
      >
        <PermanentRoleWrapper>
          <OnboardingTitle>
            Are you open to a permanent
            <FootnoteIndicator>*</FootnoteIndicator>{' '}
            {profileType === 'tech' ? 'healthcare tech' : 'nursing'} job?
          </OnboardingTitle>
          <div>
            <Button
              onClick={() => setOnboardingFields('not_looking')}
              variant="tertiary"
              style={{
                minWidth: '100px',
                marginRight: '32px',
              }}
            >
              No
            </Button>
            <Button
              onClick={() => {
                setOnboardingFields(
                  isReactivationFlow(onboardingBasePath)
                    ? 'just_started'
                    : UNSELECTED
                );
              }}
              variant="tertiary"
              style={{
                minWidth: '100px',
              }}
            >
              Yes
            </Button>
          </div>
          <Footnote className="mx-auto max-w-57.5 bg-white">
            Full time, part time, and per diem options available.
          </Footnote>
          <Button
            variant="minimal"
            onClick={() => {
              window.mixpanel.track(
                `${profileType} clicked switch role type button`
              );
              window.location.assign(
                profileType === 'tech' ? '/talent_onboard/' : '/tech_onboard/'
              );
            }}
            style={{ marginTop: $theme.sizing.unit16 }}
          >
            I&apos;m interested in
            {profileType === 'tech' ? ' nursing ' : ' healthcare tech '}
            roles
          </Button>
        </PermanentRoleWrapper>
        {onboardingTestimonial}
      </OnboardingWrapper>
    </Center>
  );
};

export default OnboardingPermanentJob;
